import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { BrandingStyleInjector, type TenantBranding } from '@/components/shared/BrandingStyleInjector';
import { AwaitingInviteScreen } from '@/features/auth/components/AwaitingInviteScreen';

// Branding genérico de alto nível para o Superadmin quando nenhum tenant está selecionado
const SUPERADMIN_BRANDING: TenantBranding = {
  tenant_id: 'global',
  logo_url: null,
  primary_color: '#0A2342',
  accent_color: '#00C2FF',
  candidate_name: null,
  party: null,
  slogan: 'Administração Global',
  app_name: 'Lumenise Base',
};

interface TenantContextType {
  tenantId: string;
  role: string;
  branding: TenantBranding;
  isSuperAdmin: boolean;
  activeTenantId: string | null;
  switchTenant: (tenantId: string | null) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading, isSuperAdmin } = useAuth();

  // tenant_id resolvido do JWT para usuários normais
  const jwtTenantId = session?.user?.app_metadata?.tenant_id as string | undefined;
  const role = session?.user?.app_metadata?.role as string | undefined;

  // Para superadmin, permitimos alternar o tenant ativo via UI
  const [activeTenantId, setActiveTenantId] = useState<string | null>(
    jwtTenantId ?? null
  );

  const switchTenant = useCallback((tenantId: string | null) => {
    setActiveTenantId(tenantId);
  }, []);

  // O tenant efetivo: para superadmin usa o selecionado; para usuários normais usa o do JWT
  const effectiveTenantId = isSuperAdmin ? activeTenantId : jwtTenantId;

  const { data: branding, isLoading: brandingLoading } = useQuery<TenantBranding>({
    queryKey: ['tenant', effectiveTenantId, 'branding'],
    queryFn: async () => {
      if (!effectiveTenantId) throw new Error('Tenant ID não definido');

      const { data, error } = await supabase
        .from('tenant_branding')
        .select('*')
        .eq('tenant_id', effectiveTenantId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!effectiveTenantId,
    staleTime: Infinity,
  });

  if (authLoading) {
    return <SplashScreen message="Carregando credenciais..." />;
  }

  if (!session) {
    return <>{children}</>;
  }

  // Superadmin sem tenant selecionado: usa branding global e entra direto
  if (isSuperAdmin && !effectiveTenantId) {
    return (
      <TenantContext.Provider
        value={{
          tenantId: 'global',
          role: 'superadmin',
          branding: SUPERADMIN_BRANDING,
          isSuperAdmin,
          activeTenantId,
          switchTenant,
        }}
      >
        <BrandingStyleInjector branding={SUPERADMIN_BRANDING} />
        {children}
      </TenantContext.Provider>
    );
  }

  // Usuário normal sem tenant_id — aguardando convite
  if (!effectiveTenantId) {
    return <AwaitingInviteScreen />;
  }

  if (brandingLoading || !branding) {
    return <SplashScreen message="Carregando identidade visual..." />;
  }

  return (
    <TenantContext.Provider
      value={{
        tenantId: effectiveTenantId,
        role: isSuperAdmin ? 'superadmin' : (role || 'lider'),
        branding,
        isSuperAdmin,
        activeTenantId,
        switchTenant,
      }}
    >
      <BrandingStyleInjector branding={branding} />
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }
  return context;
}

// Tela básica de boot/carregamento — alinhada ao design midnight
function SplashScreen({ message }: { message: string }) {
  return (
    <main className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-midnight text-ink">
      <div className="absolute inset-0 -z-10 mesh-midnight">
        <div
          className="aurora-orb aurora-orb-accent animate-aurora-1"
          style={{ width: 420, height: 420, top: '15%', left: '20%', opacity: 0.35 }}
        />
        <div
          className="aurora-orb aurora-orb-violet animate-aurora-2"
          style={{ width: 380, height: 380, bottom: '15%', right: '20%', opacity: 0.3 }}
        />
      </div>
      <div className="relative flex flex-col items-center gap-6 animate-fade-in">
        <div className="relative">
          <div className="absolute inset-0 rounded-full blur-2xl bg-accent/30" />
          <svg
            className="relative h-12 w-12 animate-spin text-accent"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path
              className="opacity-90"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted animate-pulse">
          {message}
        </p>
      </div>
    </main>
  );
}
